import { TEN_MINUTES } from '@/constants/time';
import {
  isGroupedEntries,
  readGroupedEntries,
  type GroupedInboxEntry,
} from '@/services/conversations/inboxQuestionGroups';
import type { components, paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';
import type { QuestionGroup } from '@/util/questionGroups';

export type InboxEntry = components['schemas']['InboxEntry'];

export type InboxSupportEntry = InboxEntry & { kind: 'support' };

type InboxQuery = NonNullable<paths['/v2/conversations']['get']['parameters']['query']>;

type InboxInquiry = NonNullable<InboxEntry['inquiry']>;

/** How long a refusal or an ignored grouped request is remembered before the server is asked again. */
const GROUPING_REFUSAL_MEMORY = TEN_MINUTES;

/** How the inbox is asked to group its rows. `none` is today's flat list. */
export type InboxGrouping = 'none' | 'question';

/**
 * One page of the inbox, in the shape the inquiry lists have always held.
 *
 * `inquiries` keeps its old meaning, the inquiry items in this page, so every
 * cache patch written against the inquiry list (unread, status, preview,
 * close) still applies unchanged. Support threads, which are not inquiries,
 * sit beside them. `order` is the API's order across both, by conversation id;
 * the API sorts and filters the inbox and the app keeps no rules of its own.
 *
 * A page the server grouped by question also carries `questionGroups` and the ids they own. Its `inquiries`
 * then include every thread of every inquiry group, so the same patches still find their rows. A flat page
 * has neither field, which is how the lists tell the two apart.
 */
export type InboxPage = {
  inquiries: InboxInquiry[];
  supportThreads: InboxSupportEntry[];
  order: string[];
  nextCursor: string | null;
  questionGroups?: QuestionGroup[];
  questionGroupMemberIds?: string[];
};

function isSupportEntry(entry: InboxEntry): entry is InboxSupportEntry {
  return entry.kind === 'support';
}

export function toInboxPage(entries: readonly InboxEntry[], nextCursor: string | null): InboxPage {
  return {
    inquiries: entries.flatMap((entry) => (entry.inquiry ? [entry.inquiry] : [])),
    supportThreads: entries.filter(isSupportEntry),
    order: entries.map((entry) => entry.conversationId),
    nextCursor,
  };
}

/** A page of the inbox as the server grouped it by question. */
export function toGroupedInboxPage(
  entries: readonly GroupedInboxEntry[],
  nextCursor: string | null
): InboxPage {
  const parts = readGroupedEntries(entries);

  return {
    inquiries: parts.inquiries as InboxInquiry[],
    supportThreads: parts.singles.filter(isSupportEntry),
    order: parts.order,
    nextCursor,
    questionGroups: parts.questionGroups,
    questionGroupMemberIds: parts.questionGroupMemberIds,
  };
}

/**
 * Viewers for whom the server has refused or ignored grouping, and when to ask again. A refusal
 * means the capability or the rollout flag is off, and an old server never answers a grouped
 * request, so asking on every page and refetch would only hammer it. The memory is bounded, so a flag
 * turned on later takes effect within minutes instead of at the next app restart. Keyed by viewer so
 * signing in as someone else asks afresh.
 */
const groupingUnavailableUntil = new Map<string, number>();

function isGroupingUnavailable(viewerKey: string) {
  const until = groupingUnavailableUntil.get(viewerKey);

  if (until === undefined) {
    return false;
  }

  if (Date.now() >= until) {
    groupingUnavailableUntil.delete(viewerKey);

    return false;
  }

  return true;
}

function rememberGroupingUnavailable(viewerKey: string) {
  groupingUnavailableUntil.set(viewerKey, Date.now() + GROUPING_REFUSAL_MEMORY);
}

/** Forgets what the session learned. For tests; the app never needs it. */
export function resetInboxGroupingAvailability() {
  groupingUnavailableUntil.clear();
}

function isForbidden(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'status' in error && error.status === 403;
}

/**
 * Marks a cursor that came from a grouped page. The grouped and flat routes page with cursors
 * neither can read, and a cursor one cannot read restarts the list from the top, so a page after
 * the first must go to the route that issued its cursor whatever the session has learned since.
 */
const GROUPED_CURSOR_PREFIX = 'grouped:';

/**
 * One page of the inbox. `groupBy: 'question'` asks the server to group by question; the request
 * is sent only then, so every other caller's request is the one it always was.
 *
 * Grouping is a request, not a promise. An old server answers with the flat page, which is
 * returned as it is so the lists group on the client. A 403 means this viewer may not have it, and
 * the same page is asked for flat. Either way the viewer is remembered for ten minutes, and a list
 * started in that time is flat. Only the first page decides: every later page follows the route its
 * cursor came from, so one list never mixes grouped and flat pages. A 403 on a grouped page after
 * the first is rethrown, since the flat route cannot continue from its cursor, so the query
 * refetches from the top.
 */
export async function getInboxPage(query: InboxQuery, viewerKey = ''): Promise<InboxPage> {
  const { groupBy, cursor, ...rest } = query;

  const isGroupedCursor = cursor?.startsWith(GROUPED_CURSOR_PREFIX) ?? false;

  const serverCursor = isGroupedCursor ? cursor?.slice(GROUPED_CURSOR_PREFIX.length) : cursor;

  const flatQuery = { ...rest, ...(serverCursor ? { cursor: serverCursor } : {}) };

  const asksGrouped = cursor ? isGroupedCursor : !isGroupingUnavailable(viewerKey);

  if (groupBy === 'question' && asksGrouped) {
    try {
      const { data } = await fetchClient.GET('/v2/conversations', {
        params: { query: { ...flatQuery, groupBy } },
      });

      const entries = data?.data.entries ?? [];

      const nextCursor = data?.data.nextCursor ?? null;

      if (isGroupedEntries(entries)) {
        return toGroupedInboxPage(
          entries,
          nextCursor === null ? null : `${GROUPED_CURSOR_PREFIX}${nextCursor}`
        );
      }

      rememberGroupingUnavailable(viewerKey);

      return toInboxPage(entries, nextCursor);
    } catch (error) {
      if (!isForbidden(error)) {
        throw error;
      }

      rememberGroupingUnavailable(viewerKey);

      if (cursor) {
        throw error;
      }
    }
  }

  const { data } = await fetchClient.GET('/v2/conversations', { params: { query: flatQuery } });

  return toInboxPage((data?.data.entries ?? []) as InboxEntry[], data?.data.nextCursor ?? null);
}
